import 'vite/modulepreload-polyfill';
import React, { useState } from 'react';

interface ParagraphProps {
  text: string | string[] | React.ReactNode;
  type?: string;
  htmlTag?: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
  maxChars?: number;
  maxLines?: number;
  textLimitType?: 'none' | 'characters' | 'lines';
  shouldTruncateText?: boolean;
  viewMoreText?: string;
  viewLessText?: string;
  readMorePosition?: 'inline' | 'below';
  classes?: string;
  style?: React.CSSProperties;
  enableRteStyles?: boolean;
}

export const Paragraph: React.FC<ParagraphProps> = ({
  text,
  type = '',
  htmlTag = 'p',
  maxChars = 5000,
  maxLines = 0,
  textLimitType = 'none',
  shouldTruncateText = false,
  viewMoreText = 'Show More',
  viewLessText = 'Show Less',
  readMorePosition = 'inline', // 'inline' or 'below'
  classes = '',
  style = {},
  enableRteStyles = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper function to strip HTML tags for accurate character counting
  const stripHtmlTags = (str: string) => str.replace(/<[^>]*>/g, '');

  // Determine if truncation should be applied based on text limit type
  const shouldTruncateByChars = shouldTruncateText && textLimitType === 'characters' && maxChars > 0 && typeof text === 'string' && stripHtmlTags(text).length > maxChars;
  const shouldTruncateByLines = (shouldTruncateText || textLimitType === 'lines') && textLimitType === 'lines' && maxLines > 0 && typeof text === 'string';
  const shouldTruncate = shouldTruncateByChars || shouldTruncateByLines;

  const getTruncatedTextByChars = (str: string, maxChars: number) => {
    const plainText = stripHtmlTags(str);
    if (plainText.length <= maxChars) return str;

    let charCount = 0;
    let result = '';
    let inTag = false;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];

      if (char === '<') {
        inTag = true;
      }

      result += char;

      if (!inTag) {
        charCount++;
        if (charCount >= maxChars) {
          return result + '...';
        }
      }

      if (char === '>') {
        inTag = false;
      }
    }

    return result;
  };

  const getTruncatedText = () => {
    if (!shouldTruncateByChars || typeof text !== 'string') return text;
    return getTruncatedTextByChars(text, maxChars);
  };

  const truncatedText = getTruncatedText();

  // Generate line-clamp styles for line-based truncation
  const getLineClampStyles = () => {
    if (shouldTruncateByLines && !isExpanded) {
      return {
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordWrap: 'break-word'
      };
    }
    return {};
  };

  const renderText = () => {
    // Create the dynamic HTML element based on htmlTag
    const createTag = (content: string, key?: number) => {
      const lineClampStyles = getLineClampStyles();
      
      const props = {
        className: `${type} ${classes} ${enableRteStyles ? 'rte' : ''}`.trim(),
        style: { ...style, ...lineClampStyles },
        dangerouslySetInnerHTML: { __html: content },
        ...(key !== undefined && { key })
      };

      switch (htmlTag) {
        case 'h1':
          return <h1 {...props} />;
        case 'h2':
          return <h2 {...props} />;
        case 'h3':
          return <h3 {...props} />;
        case 'h4':
          return <h4 {...props} />;
        case 'h5':
          return <h5 {...props} />;
        case 'h6':
          return <h6 {...props} />;
        case 'div':
          return <div {...props} />;
        case 'span':
          return <span {...props} />;
        case 'p':
        default:
          return <p {...props} />;
      }
    };

    const createSimpleTag = (content: any, key?: number) => {
      const lineClampStyles = getLineClampStyles();
      const baseProps = {
        className: `${type} ${classes} ${enableRteStyles ? 'rte' : ''}`.trim(),
        style: { ...style, ...lineClampStyles },
        ...(key !== undefined && { key })
      };

      const props = typeof content === 'string'
        ? { ...baseProps, dangerouslySetInnerHTML: { __html: content } }
        : baseProps;

      switch (htmlTag) {
        case 'h1':
          return typeof content === 'string' ? <h1 {...props} /> : <h1 {...props}>{content}</h1>;
        case 'h2':
          return typeof content === 'string' ? <h2 {...props} /> : <h2 {...props}>{content}</h2>;
        case 'h3':
          return typeof content === 'string' ? <h3 {...props} /> : <h3 {...props}>{content}</h3>;
        case 'h4':
          return typeof content === 'string' ? <h4 {...props} /> : <h4 {...props}>{content}</h4>;
        case 'h5':
          return typeof content === 'string' ? <h5 {...props} /> : <h5 {...props}>{content}</h5>;
        case 'h6':
          return typeof content === 'string' ? <h6 {...props} /> : <h6 {...props}>{content}</h6>;
        case 'div':
          return typeof content === 'string' ? <div {...props} /> : <div {...props}>{content}</div>;
        case 'span':
          return typeof content === 'string' ? <span {...props} /> : <span {...props}>{content}</span>;
        case 'p':
        default:
          return typeof content === 'string' ? <p {...props} /> : <p {...props}>{content}</p>;
      }
    };

    if (typeof text === 'string') {
      // Render as plain text with truncation and expansion
      if (shouldTruncate) {
        if (shouldTruncateByLines) {
          // For line-clamp, we don't modify the text content, just apply CSS
          const displayText = text;
          const linkText = isExpanded ? viewLessText : viewMoreText;

          if (readMorePosition === 'inline') {
            // For line-clamp with inline, we need a different approach
            return (
              <div className={`${classes} ${type}`}>
                {createSimpleTag(displayText)}
                <a
                  onClick={toggleExpand}
                  className='underline cursor-pointer inline-block ml-1'
                >
                  {linkText}
                </a>
              </div>
            );
          } else {
            // Below positioning: Read More appears as a separate block element
            return (
              <div className={`${classes} ${type}`}>
                {createSimpleTag(displayText)}
                <a
                  onClick={toggleExpand}
                  className='underline mt-2 cursor-pointer block'
                >
                  {linkText}
                </a>
              </div>
            );
          }
        } else {
          // Character-based truncation (existing logic)
          const displayText = isExpanded ? text : truncatedText;
          const linkText = isExpanded ? viewLessText : viewMoreText;

          if (readMorePosition === 'inline') {
            // Inline positioning: Read More appears within the same element
            const contentWithLink = `${displayText} <a class="underline cursor-pointer" data-toggle-expand="true">${linkText}</a>`;

            return (
              <div onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.hasAttribute('data-toggle-expand')) {
                  e.preventDefault();
                  toggleExpand();
                }
              }}>
                {createSimpleTag(contentWithLink)}
              </div>
            );
          } else {
            // Below positioning: Read More appears as a separate block element
            return (
              <div className={`${classes} ${type}`}>
                {createSimpleTag(displayText)}
                <a
                  onClick={toggleExpand}
                  className='underline mt-2 cursor-pointer block'
                >
                  {linkText}
                </a>
              </div>
            );
          }
        }
      } else {
        return createSimpleTag(text);
      }
    } else if (Array.isArray(text)) {
      // Render each item in the array, checking if it's a valid React element or plain text
      return text.map((item, index) =>
        React.isValidElement(item) ? <React.Fragment key={index}>{item}</React.Fragment> : createSimpleTag(item, index)
      );
    } else {
      // Handle non-string, non-array cases as plain text
      return createSimpleTag(text);
    }
  };

  return <div>{renderText()}</div>;
};

export default Paragraph;
